import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Brain, Users, Smile } from 'lucide-react';
import { useProgress } from '../context/ProgressContext';
export function EmotionalIntelligencePage() {
  const navigate = useNavigate();
  const {
    progress
  } = useProgress();
  const modules = [{
    id: 'self-awareness',
    title: 'Understanding Your Emotions',
    icon: Brain,
    description: "Learn to identify and understand what you're feeling",
    color: 'from-blue-500 to-indigo-500',
    content: ['Emotions are natural signals that tell us about our needs and experiences', 'There are no "bad" emotions - all feelings are valid and important', 'Common emotions: happiness, sadness, anger, fear, surprise, disgust', 'Your body gives clues: tight chest (anxiety), warm face (embarrassment), heavy limbs (sadness)', 'Keep an emotion journal: "Today I felt ___ when ___ happened"']
  }, {
    id: 'self-regulation',
    title: 'Managing Strong Feelings',
    icon: Heart,
    description: 'Healthy ways to cope with intense emotions',
    color: 'from-pink-500 to-rose-500',
    content: ['Deep breathing: Inhale for 4, hold for 4, exhale for 4', 'Physical release: Exercise, dance, punch a pillow (not people!)', 'Talk it out: Share with trusted friends, family, or counselors', 'Creative expression: Draw, write, make music about your feelings', 'Time and space: Sometimes you need to step away and cool down', 'Remember: Feeling emotions is healthy; how you express them matters']
  }, {
    id: 'empathy',
    title: 'Understanding Others',
    icon: Users,
    description: 'Develop empathy and emotional intelligence',
    color: 'from-teal-500 to-green-500',
    content: ["Empathy = Understanding how others feel, even if you haven't experienced it", "Listen actively: Give full attention, don't interrupt, ask questions", 'Notice body language: Crossed arms, avoiding eye contact, fidgeting', 'Validate feelings: "That sounds really hard" instead of "Don\'t feel that way"', 'Different people, different reactions: What hurts you might not hurt someone else, and vice versa', 'Practice perspective-taking: "How would I feel if this happened to me?"']
  }, {
    id: 'self-esteem',
    title: 'Building Confidence',
    icon: Smile,
    description: 'Develop healthy self-worth and confidence',
    color: 'from-yellow-500 to-orange-500',
    content: ['Self-esteem = How much you value yourself (not how others value you)', 'Challenge negative self-talk: Replace "I\'m stupid" with "I made a mistake, I can learn"', 'Celebrate small wins: Did homework? Helped a friend? That counts!', "Your worth isn't determined by: grades, looks, popularity, achievements", 'Focus on what you CAN control: effort, attitude, kindness, growth', 'Surround yourself with people who lift you up, not tear you down', 'Remember: You are enough, exactly as you are']
  }];
  return <div className="min-h-screen w-full bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-slate-900 dark:via-purple-950 dark:to-pink-950 pb-20 md:pb-8" data-id="element-370">
      <header className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-b border-blue-100 dark:border-slate-700 sticky top-0 z-10" data-id="element-371">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between" data-id="element-372">
          <button onClick={() => navigate('/home')} className="flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300" data-id="element-373">
            <ArrowLeft className="w-5 h-5" data-id="element-374" />
            <span className="font-medium" data-id="element-375">Home</span>
          </button>
          <h1 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-376">
            Emotional Intelligence
          </h1>
          <div className="w-20" data-id="element-377" />
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 py-8" data-id="element-378">
        <div className="text-center mb-8" data-id="element-379">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3" data-id="element-380">
            Master Your Emotions, {progress.preferences.name || 'Friend'}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" data-id="element-381">
            Understanding and managing emotions is a superpower. Learn to
            navigate your feelings and build emotional strength.
          </p>
        </div>
        <div className="space-y-6" data-id="element-382">
          {modules.map(module => {
          const Icon = module.icon;
          return <div key={module.id} className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg overflow-hidden" data-id="element-383">
                <div className={`h-24 bg-gradient-to-br ${module.color} flex items-center px-6`} data-id="element-384">
                  <Icon className="w-10 h-10 text-white mr-4" data-id="element-385" />
                  <div data-id="element-386">
                    <h3 className="text-2xl font-bold text-white" data-id="element-387">
                      {module.title}
                    </h3>
                    <p className="text-white/90 text-sm" data-id="element-388">
                      {module.description}
                    </p>
                  </div>
                </div>
                <div className="p-6" data-id="element-389">
                  <ul className="space-y-3" data-id="element-390">
                    {module.content.map((item, index) => <li key={index} className="flex items-start space-x-3" data-id="element-391">
                        <div className={`w-6 h-6 bg-gradient-to-br ${module.color} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`} data-id="element-392">
                          <span className="text-white text-xs font-bold" data-id="element-393">
                            {index + 1}
                          </span>
                        </div>
                        <p className="text-gray-700 dark:text-gray-300" data-id="element-394">
                          {item}
                        </p>
                      </li>)}
                  </ul>
                </div>
              </div>;
        })}
        </div>
        <div className="mt-8 bg-blue-50 dark:bg-slate-800 border-l-4 border-blue-500 rounded-lg p-6" data-id="element-395">
          <h4 className="font-bold text-blue-900 dark:text-blue-300 mb-2" data-id="element-396">
            Practice Makes Progress
          </h4>
          <p className="text-blue-800 dark:text-blue-200" data-id="element-397">
            Emotional intelligence isn't something you're born with - it's a
            skill you develop. Be patient with yourself as you learn to
            understand and manage your emotions better!
          </p>
        </div>
      </div>
    </div>;
}